/**
 * 统一树形结构构建器
 * 整合：微服务 -> 页面 -> 组件 -> 子组件 -> 数据集 -> 字段
 */

import type { Component, Dataset } from '../../../services/aiConfigService';
import type { ServiceInfo } from '@ms/shared-auth';

export interface TreeNode {
  key: string;
  title: string;
  type: 'service' | 'page' | 'component' | 'dataset' | 'field';
  data?: any;
  children?: TreeNode[];
  isLeaf?: boolean;
}

/**
 * 构建统一的树形结构
 */
export const buildUnifiedTree = (
  services: ServiceInfo[],
  components: Component[],
  datasets: Dataset[]
): TreeNode[] => {
  const tree: TreeNode[] = [];

  // 按服务分组组件
  const serviceMap = new Map<string, Map<string, Component[]>>();
  
  components.forEach(component => {
    if (!component.serviceName) return;
    
    if (!serviceMap.has(component.serviceName)) {
      serviceMap.set(component.serviceName, new Map());
    }
    const pageMap = serviceMap.get(component.serviceName)!;
    if (!pageMap.has(component.pageId)) {
      pageMap.set(component.pageId, []);
    }
    pageMap.get(component.pageId)!.push(component);
  });

  // 构建服务节点
  services.forEach(service => {
    const serviceNode: TreeNode = {
      key: `service:${service.serviceName}`,
      title: service.displayName || service.serviceName,
      type: 'service',
      data: service,
      children: [],
    };

    // 获取该服务下的页面
    const pageMap = serviceMap.get(service.serviceName);
    if (pageMap) {
      pageMap.forEach((pageComponents, pageId) => {
        const pageNode: TreeNode = {
          key: `page:${service.serviceName}:${pageId}`,
          title: pageId,
          type: 'page',
          data: { serviceName: service.serviceName, pageId },
          children: [],
        };

        // 构建组件树（支持父子关系）
        const rootComponents = pageComponents.filter(c => !c.parentId);
        
        const buildComponentNode = (component: Component): TreeNode => {
          const componentNode: TreeNode = {
            key: `component:${component.id}`,
            title: component.name || component.componentId,
            type: 'component',
            data: component,
            children: [],
          };

          // 添加子组件
          const childComponents = pageComponents.filter(c => c.parentId === component.id);
          childComponents.forEach(child => {
            componentNode.children!.push(buildComponentNode(child));
          });

          // 添加关联的数据集
          if (component.datasets && component.datasets.length > 0) {
            component.datasets.forEach((cd: any) => {
              const datasetId = cd.datasetId || cd.dataset?.datasetId;
              const dataset = datasets.find(d => d.datasetId === datasetId);
              if (dataset) {
                const datasetNode: TreeNode = {
                  key: `dataset:${dataset.datasetId}:${component.id}`,
                  title: dataset.name || dataset.datasetId,
                  type: 'dataset',
                  data: dataset,
                  children: [],
                };

                // 添加字段
                if (dataset.fields && dataset.fields.length > 0) {
                  dataset.fields.forEach((field: any) => {
                    datasetNode.children!.push({
                      key: `field:${field.fieldId}:${dataset.datasetId}`,
                      title: `${field.fieldName} (${field.fieldType})`,
                      type: 'field',
                      data: field,
                      isLeaf: true,
                    });
                  });
                }

                componentNode.children!.push(datasetNode);
              }
            });
          }

          return componentNode;
        };

        rootComponents.forEach(component => {
          pageNode.children!.push(buildComponentNode(component));
        });

        serviceNode.children!.push(pageNode);
      });
    }

    tree.push(serviceNode);
  });

  return tree;
};
